import { useState, useEffect, useCallback, useMemo } from 'react';
import { FieldHelp } from '@/features/guided-help';
import {
  Plus,
  Calendar,
  Clock,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
  Building2,
  CalendarDays,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Info,
  Flag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { WizardStepProps, ClubScheduleSelection, ClubSchedulesSlice } from '../../../engine/types';
import { TournamentFormat, type Club } from '@/types/tournament.model';
import { getClubsByOwner, getClubsByManager } from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { CLUB_STATUS } from '@/types/club.model';
import {
  getSchedulesByClub,
  createSchedule,
} from '@/api/schedule/scheduleApi';
import { SCHEDULE_FIELDS } from '@/api/schedule/schedule-fields.config';
import {
  getDayOfWeek,
  isRegularScheduleBlock,
  type Schedule as ApiSchedule,
  type WeeklyGridConfig,
} from '@/types/schedule.model';
import { WeeklyGridPicker } from '@/features/tournament-wizard/components/weekly-grid-picker/WeeklyGridPicker';
import { dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';
import { formatLocalYmd } from '@/utils/localDateOnly';
import { handleGlobalError } from '@/utils/error-handler';
import { getCourtsCountForSport } from '@/utils/club-utils';
import { formatDateForUser } from '@/utils/formatters';
import {
  earliestKnockoutStart,
  formatKnockoutStart,
  knockoutBlockLabel,
  knockoutStartOf,
} from '@/utils/tournament-schedule';
import { CreateScheduleBlockForm } from '@/features/tournament-wizard/components/create-schedule-block';
import { DateInputWithCalendar } from '@/components/ui/date-input-with-calendar';
import { Label } from '@/components/ui/label';
import type { WeekDay } from '@/types/tournament-round.model';
import type { ScheduleDatesMode } from '@/features/tournament-wizard/components/create-schedule-block';
import {
  filterTimeBlocksByMatchDuration,
  isScheduleStartAfterDeadline,
  isScheduleCompatibleWithTournament,
  type Schedule,
  type TimeBlock,
} from '../../../utils/scheduleEligibility';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** "lunes", "sábados": the plural weekday for sentences like "los sábados". */
function pluralDayLabel(day: string): string {
  const label = (dayOfWeekMap[day] ?? day).toLowerCase();
  return /[oa]$/.test(label) ? `${label}s` : label;
}

/** Clears the LONG grid choice (club removed, match duration changed). */
const NO_WEEKLY_GRID: Partial<ClubSchedulesSlice> = {
  weeklyGridId: undefined,
  weeklyGridName: undefined,
  weeklyGridConfig: null,
  weeklyScheduleTemplate: undefined,
};

/** Notice for a grid shared with other tournaments: this tournament adopts their weekly settings. */
function describeAdoptedConfig(config: WeeklyGridConfig): string {
  const start = pluralDayLabel(config.startDayOfWeek);
  const deadline =
    config.availabilityDeadlineDay && config.availabilityDeadlineTime
      ? ` y la carga de disponibilidad cierra los ${pluralDayLabel(config.availabilityDeadlineDay)} a las ${config.availabilityDeadlineTime}`
      : '';
  return `Esta grilla la comparten otros torneos, así que este torneo adopta su configuración: las fechas arrancan los ${start}${deadline}. En Configuración esos campos quedan bloqueados.`;
}

function transformApiScheduleToUI(apiSchedule: ApiSchedule): Schedule {
  const timeBlocks: TimeBlock[] = [];
  let blockIdCounter = 0;

  const scheduleEntries = apiSchedule.schedule || [];
  
  scheduleEntries.forEach((entry) => {
    entry.timeRange.forEach((range) => {
      timeBlocks.push({
        id: `tb-${apiSchedule.id}-${blockIdCounter++}`,
        date: entry.date,
        startTime: range.startTime,
        endTime: range.endTime,
        courtCount: range.courtsQuantity,
      });
    });
  });

  const club = apiSchedule.club;
  const clubAddress =
    club?.address?.street && club?.address?.city
      ? `${club.address.street}, ${club.address.city}`
      : club?.address?.street || '';

  return {
    id: apiSchedule.id,
    title: apiSchedule.name || 'Sin nombre',
    clubId: club?.id || '',
    clubName: club?.name || 'Club desconocido',
    clubAddress,
    timeBlocks,
    isReusable: true,
    createdAt: apiSchedule.createdAt || new Date().toISOString(),
    tournaments: apiSchedule.tournaments,
    knockoutStart: knockoutStartOf(apiSchedule),
  };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface ClubSchedulesStepProps
  extends WizardStepProps<ClubSchedulesSlice> {
  /**
   * When set, the club list is frozen: only these clubs are offered. Used by the
   * details-page edit modal, where the backend ignores `clubs` on tournaments that
   * already have registrations.
   */
  allowedClubIds?: string[];
  /**
   * True inside the details-page edit modal (not the wizard). A LONG's weekly grid is fixed once the
   * tournament exists (the backend ignores `schedule` when patching a LONG), so the picker is hidden.
   */
  isEditing?: boolean;
}

export function ClubSchedulesStep({
  data,
  updateData,
  draft,
  allowedClubIds,
  isEditing = false,
}: ClubSchedulesStepProps) {
  // Match duration from tournament config (previous step)
  const matchDuration = draft?.tournamentConfig?.matchDuration ?? 60;
  const signupDeadlineDate = draft?.tournamentConfig?.signupDeadlineDate ?? null;
  const isLongFormat = draft?.fixtureConfig?.format === TournamentFormat.LONG;
  const sportUid = draft?.sport?.sport?.uid;
  const tournamentFormat = draft?.fixtureConfig?.format;

  // Available clubs
  const [availableClubs, setAvailableClubs] = useState<Club[]>([]);
  const [isLoadingClubs, setIsLoadingClubs] = useState(true);
  const [clubsError, setClubsError] = useState<string | null>(null);

  // Schedules cache
  const [schedulesCache, setSchedulesCache] = useState<Record<string, Schedule[]>>({});
  const [loadingClubSchedules, setLoadingClubSchedules] = useState<string | null>(null);

  // UI state
  const [showAddClub, setShowAddClub] = useState(false);
  const [clubSearchQuery, setClubSearchQuery] = useState('');

  // Get data
  const clubSchedules = data?.clubSchedules ?? [];
  const validForMatchDuration = data?.validForMatchDuration ?? null;

  // Normalize selection: only keep schedule IDs that still have blocks matching current matchDuration.
  // This fixes the case where duration changed but the slice wasn't reset yet (e.g. async update).
  const effectiveClubSchedules = useMemo(() => {
    return clubSchedules.map((cs) => {
      const schedules = schedulesCache[cs.clubId];
      if (schedules === undefined) return cs;
      const validScheduleIds = schedules
        .filter((s) =>
          filterTimeBlocksByMatchDuration(s.timeBlocks, matchDuration).length > 0 &&
          isScheduleStartAfterDeadline(s, signupDeadlineDate) &&
          isScheduleCompatibleWithTournament(s, { sportUid, format: tournamentFormat })
        )
        .map((s) => s.id);
      return {
        ...cs,
        scheduleIds: cs.scheduleIds.filter((id) => validScheduleIds.includes(id)),
      };
    });
  }, [clubSchedules, schedulesCache, matchDuration, signupDeadlineDate, sportUid, tournamentFormat]);

  // Persist normalized selection when some selected blocks no longer match current duration
  const hasInvalidSelections = useMemo(() => {
    return clubSchedules.some(
      (cs, i) =>
        cs.scheduleIds.length !== effectiveClubSchedules[i].scheduleIds.length ||
        cs.scheduleIds.some((id, j) => id !== effectiveClubSchedules[i].scheduleIds[j])
    );
  }, [clubSchedules, effectiveClubSchedules]);

  useEffect(() => {
    if (hasInvalidSelections) {
      updateData({
        clubSchedules: effectiveClubSchedules,
        validForMatchDuration: effectiveClubSchedules.some((cs) => cs.scheduleIds.length > 0)
          ? matchDuration
          : null,
      });
    }
  }, [hasInvalidSelections, effectiveClubSchedules, matchDuration, updateData]);

  // Reset step when match duration changed: clear all selections and stamp new duration
  useEffect(() => {
    if (validForMatchDuration !== null && validForMatchDuration !== matchDuration) {
      updateData({ clubSchedules: [], validForMatchDuration: matchDuration, ...NO_WEEKLY_GRID });
      setSchedulesCache({});
    }
  }, [matchDuration, validForMatchDuration, updateData]);

  // Fetch clubs
  const fetchClubs = useCallback(async () => {
    setIsLoadingClubs(true);
    setClubsError(null);

    try {
      const [ownerClubs, managerClubs] = await Promise.allSettled([
        getClubsByOwner(CLUB_FIELDS.LIST),
        getClubsByManager(CLUB_FIELDS.LIST),
      ]);

      const ownerClubsResult =
        ownerClubs.status === 'fulfilled'
          ? (ownerClubs.value as unknown as Club[])
          : [];

      const managerClubsResult =
        managerClubs.status === 'fulfilled'
          ? (managerClubs.value as unknown as Club[])
          : [];

      const allClubs = [...ownerClubsResult, ...managerClubsResult];
      const validClubs = allClubs.filter((club) => {
        const isEnabled = club.status === CLUB_STATUS.ENABLED;
        const hasCourts = club.courts && club.courts.length >= 1;
        return isEnabled && hasCourts;
      });

      const uniqueClubs = validClubs.filter(
        (club, index, self) =>
          index === self.findIndex((c) => c.id === club.id)
      );

      setAvailableClubs(uniqueClubs);
    } catch (err) {
      setClubsError('No se pudieron cargar los clubs');
      handleGlobalError(err, 'Error', 'No se pudieron cargar los clubs');
    } finally {
      setIsLoadingClubs(false);
    }
  }, []);

  // Fetch schedules for a specific club
  const fetchSchedulesForClub = useCallback(async (clubId: string) => {
    // LONG doesn't use this list: WeeklyGridPicker loads the club's grids on its own.
    if (isLongFormat || schedulesCache[clubId]) return;

    setLoadingClubSchedules(clubId);

    try {
      const apiSchedules = await getSchedulesByClub(clubId, SCHEDULE_FIELDS.WITH_TOURNAMENTS);
      const schedulesArray = Array.isArray(apiSchedules) ? apiSchedules : [];
      const uiSchedules = schedulesArray
        // Weekly grids and week blocks are LONG-only; this list feeds the SHORT/AMERICAN block picker.
        .filter(isRegularScheduleBlock)
        .map((s) => transformApiScheduleToUI(s));
      
      setSchedulesCache(prev => ({
        ...prev,
        [clubId]: uiSchedules,
      }));
    } catch (err) {
      console.error('Error fetching schedules:', err);
      handleGlobalError(err, 'Error', 'No se pudieron cargar los bloques de horario');
    } finally {
      setLoadingClubSchedules(null);
    }
  }, [schedulesCache, isLongFormat]);

  // Initialize with defaults
  useEffect(() => {
    if (!data) {
      updateData({
        clubSchedules: [],
        validForMatchDuration: null,
      });
    }
  }, [data, updateData]);

  // Helper: compute validForMatchDuration from current clubSchedules
  const getValidForMatchDuration = useCallback(
    (nextClubSchedules: typeof clubSchedules) => {
      const hasSelection = nextClubSchedules.some((cs) => cs.scheduleIds.length > 0);
      return hasSelection ? matchDuration : null;
    },
    [matchDuration]
  );

  // Load clubs on mount
  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  // Load schedules for selected clubs
  useEffect(() => {
    clubSchedules.forEach(cs => {
      if (!schedulesCache[cs.clubId]) {
        fetchSchedulesForClub(cs.clubId);
      }
    });
  }, [clubSchedules, schedulesCache, fetchSchedulesForClub]);

  // Handlers
  const handleAddClub = (club: Club) => {
    if (clubSchedules.some(cs => cs.clubId === club.id)) return;

    const newClubSchedule: ClubScheduleSelection = {
      clubId: club.id,
      club: club,
      scheduleIds: [],
    };

    const next = [...clubSchedules, newClubSchedule];
    updateData({
      clubSchedules: next,
      validForMatchDuration: getValidForMatchDuration(next),
    });

    fetchSchedulesForClub(club.id);
    setShowAddClub(false);
    setClubSearchQuery('');
  };

  const handleRemoveClub = (clubId: string) => {
    const next = clubSchedules.filter(cs => cs.clubId !== clubId);
    updateData({
      clubSchedules: next,
      validForMatchDuration: getValidForMatchDuration(next),
      ...NO_WEEKLY_GRID,
    });
  };

  const handleToggleSchedule = (clubId: string, scheduleId: string) => {
    const clubScheduleIndex = clubSchedules.findIndex(cs => cs.clubId === clubId);
    if (clubScheduleIndex === -1) return;

    const currentSchedules = clubSchedules[clubScheduleIndex].scheduleIds;
    const isSelected = currentSchedules.includes(scheduleId);

    const newScheduleIds = isSelected
      ? currentSchedules.filter(id => id !== scheduleId)
      : [...currentSchedules, scheduleId];

    const updatedClubSchedules = [...clubSchedules];
    updatedClubSchedules[clubScheduleIndex] = {
      ...updatedClubSchedules[clubScheduleIndex],
      scheduleIds: newScheduleIds,
    };

    updateData({
      clubSchedules: updatedClubSchedules,
      validForMatchDuration: getValidForMatchDuration(updatedClubSchedules),
    });
  };

  const handleScheduleCreated = async (clubId: string, newSchedule: Schedule) => {
    setSchedulesCache(prev => ({
      ...prev,
      [clubId]: [...(prev[clubId] || []), newSchedule],
    }));

    handleSelectSchedule(clubId, newSchedule.id);
  };

  /**
   * Selects a block, as opposed to toggling it. Creating a block means "use
   * this one", which is not the same request as "flip whatever state it is in":
   * routed through the toggle, any path where the id is already selected -- the
   * normalization effect re-persisting effectiveClubSchedules, or a second
   * onScheduleCreated -- silently deselects the block the user just created,
   * leaving the step invalid and "Siguiente" disabled with nothing on screen
   * explaining why.
   */
  const handleSelectSchedule = (clubId: string, scheduleId: string) => {
    const clubScheduleIndex = clubSchedules.findIndex(cs => cs.clubId === clubId);
    if (clubScheduleIndex === -1) return;

    const currentSchedules = clubSchedules[clubScheduleIndex].scheduleIds;
    if (currentSchedules.includes(scheduleId)) return;

    const updatedClubSchedules = [...clubSchedules];
    updatedClubSchedules[clubScheduleIndex] = {
      ...updatedClubSchedules[clubScheduleIndex],
      scheduleIds: [...currentSchedules, scheduleId],
    };

    updateData({
      clubSchedules: updatedClubSchedules,
      validForMatchDuration: getValidForMatchDuration(updatedClubSchedules),
    });
  };

  // AMERICAN tournaments are played in a single day: one click is enough.
  const dateSelectionMode: ScheduleDatesMode =
    draft?.fixtureConfig?.format === TournamentFormat.AMERICAN ? 'single' : 'range';

  // LONG: the (single) selected club is the venue for the weekly grid.
  const longClub = effectiveClubSchedules[0]?.club ?? null;

  const weekStartDateValue = data?.weekStartDate
    ? new Date(data.weekStartDate + 'T00:00:00')
    : null;

  // LONG: first round must start after the signup deadline (day after).
  const longMinStartDate = useMemo(() => {
    if (!signupDeadlineDate) return undefined;
    const d = new Date(signupDeadlineDate);
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [signupDeadlineDate]);

  const handleWeekStartChange = useCallback(
    (date: Date | null) => {
      updateData({
        weekStartDate: date ? formatLocalYmd(date) : undefined,
      });
    },
    [updateData],
  );

  const gridConfig = data?.weeklyGridConfig ?? null;

  // A shared grid fixes the weekday rounds start on: any other day gives weeks the siblings don't have.
  const isAllowedStartDay = useCallback(
    (date: Date) => !gridConfig || getDayOfWeek(date) === gridConfig.startDayOfWeek,
    [gridConfig],
  );

  const handleSelectGrid = useCallback(
    (grid: ApiSchedule) => {
      const config = grid.weeklyGridConfig ?? null;
      const currentStart = data?.weekStartDate;
      const startStillFits =
        !config ||
        !currentStart ||
        getDayOfWeek(new Date(currentStart + 'T00:00:00')) === config.startDayOfWeek;
      updateData({
        weeklyGridId: grid.id,
        weeklyGridName: grid.name,
        weeklyGridConfig: config,
        // Only for the summary; the payload sends the grid id.
        weeklyScheduleTemplate: (grid.schedule ?? []).flatMap((entry) =>
          entry.timeRange.map((tr) => ({
            dayOfWeek: entry.dayOfWeek as WeekDay,
            startTime: tr.startTime,
            endTime: tr.endTime,
            courtsQuantity: tr.courtsQuantity,
          })),
        ),
        weekStartDate: startStillFits ? currentStart : undefined,
        validForMatchDuration: matchDuration,
      });
    },
    [data?.weekStartDate, matchDuration, updateData],
  );

  // Narrowing happens here and not inside fetchClubs: that callback has empty deps,
  // and a prop array changing identity every render would make it refetch in a loop.
  const selectableClubs = useMemo(
    () =>
      allowedClubIds
        ? availableClubs.filter((club) => allowedClubIds.includes(club.id))
        : availableClubs,
    [availableClubs, allowedClubIds],
  );

  // Clubs that could still be added: they have courts for this sport and are not selected yet
  const addableClubs = selectableClubs.filter(club => {
    const courtCountForSport = sportUid ? getCourtsCountForSport(club, sportUid) : (club.courts?.length ?? 0);
    if (courtCountForSport < 1) return false;

    return !clubSchedules.some(cs => cs.clubId === club.id);
  });

  // LONG builds the weekly grid from a single venue (longClub), so extra clubs would be
  // inert for scheduling: they'd show in the list but never reach weeklyScheduleTemplate.
  const canAddMoreClubs =
    addableClubs.length > 0 && !(isLongFormat && effectiveClubSchedules.length > 0);

  // Filter clubs for search
  const filteredClubs = clubSearchQuery
    ? addableClubs.filter(club => {
        const query = clubSearchQuery.toLowerCase();
        return (
          club.name.toLowerCase().includes(query) ||
          club.address?.city?.toLowerCase().includes(query)
        );
      })
    : addableClubs;

  // Loading state
  if (isLoadingClubs) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Cargando clubs...</p>
      </div>
    );
  }

  // Error state
  if (clubsError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="rounded-full bg-red-100 p-3">
          <AlertCircle className="h-8 w-8 text-red-600" />
        </div>
        <p className="mt-4 font-medium text-red-900">{clubsError}</p>
        <Button variant="outline" className="mt-4" onClick={fetchClubs}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reintentar
        </Button>
      </div>
    );
  }

  // No clubs available
  if (selectableClubs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="rounded-full bg-amber-100 p-3">
          <Building2 className="h-8 w-8 text-amber-600" />
        </div>
        <p className="mt-4 font-medium text-gray-900">No tienes clubs disponibles</p>
        <p className="mt-2 text-sm text-gray-500">
          Necesitas tener al menos un club con canchas para crear un torneo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center" data-guide-id="wizard.schedules.clubs">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Building2 className="h-8 w-8 text-blue-600" />
        </div>
        <div className="flex items-center justify-center gap-2">
          <h2 className="text-2xl font-bold text-gray-900">
            {isLongFormat ? 'Horarios de las fechas' : 'Clubs y Horarios'}
          </h2>
          <FieldHelp topicId="wizard.schedules.clubs" />
        </div>
        <p className="mt-2 text-gray-600">
          {isLongFormat
            ? 'Seleccioná los horarios para la primera jornada. Las semanas siguientes se abrirán automáticamente con estos mismos horarios.'
            : 'Selecciona los clubs y los horarios disponibles para tu torneo'}
        </p>
      </div>

      {/* Informational callout for LONG */}
      {isLongFormat && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Horarios para torneos largos</p>
            <p className="mt-1">
              Los horarios que configures acá se usarán como base para todas las jornadas. Podrás
              editarlos semana a semana desde la sección "Fechas" del torneo.
            </p>
          </div>
        </div>
      )}

      {/* Match duration hint (per-date picker only; not shown for LONG) */}
      {!isLongFormat && (
        <p
          className="text-center text-sm text-gray-600"
          data-guide-id="wizard.schedules.blocks"
        >
          <FieldHelp
            topicId="wizard.schedules.blocks"
            className="mr-1 align-text-bottom"
          />
          Solo se muestran bloques de horario con duración de <strong>{matchDuration} minutos</strong> (igual a la duración de partido configurada)
          {signupDeadlineDate && (
            <>
              {' '}
              y con fecha posterior al cierre de inscripción (<strong>
                {formatDateForUser(
                  signupDeadlineDate instanceof Date
                    ? signupDeadlineDate.toISOString()
                    : String(signupDeadlineDate),
                )}
              </strong>)
            </>
          )}
          .
        </p>
      )}

      {/* Summary Card (per-date schedules; not shown for LONG) */}
      {!isLongFormat && effectiveClubSchedules.length > 0 && (
          <div data-guide-id="wizard.schedules.dates" className="space-y-1">
            <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
              De acá salen las fechas del torneo
              <FieldHelp topicId="wizard.schedules.dates" />
            </div>
            <SummaryCard
              clubSchedules={effectiveClubSchedules}
              schedulesCache={schedulesCache}
              matchDuration={matchDuration}
              showKnockouts={tournamentFormat === TournamentFormat.SHORT}
            />
          </div>
      )}

      {/* Add Club Section (moved to top of list) */}
      {canAddMoreClubs && (!showAddClub ? (
        <Button
          variant="outline"
          className="w-full border-dashed border-2 py-6"
          onClick={() => setShowAddClub(true)}
        >
          <Plus className="mr-2 h-5 w-5" />
          Agregar Club
        </Button>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Seleccionar Club</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowAddClub(false);
                  setClubSearchQuery('');
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Buscar club..."
                  value={clubSearchQuery}
                  onChange={(e) => setClubSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="max-h-64 space-y-2 overflow-y-auto">
                {filteredClubs.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500">
                    {clubSearchQuery
                      ? 'No se encontraron clubs'
                      : 'Todos los clubs ya fueron agregados'}
                  </p>
                ) : (
                  filteredClubs.map((club) => (
                    <button
                      key={club.id}
                      className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-gray-50"
                      onClick={() => handleAddClub(club)}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {club.name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {club.address?.city || 'Sin ubicación'} · {sportUid ? getCourtsCountForSport(club, sportUid) : (club.courts?.length || 0)} canchas
                        </p>
                      </div>
                      <Plus className="h-5 w-5 text-gray-400" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Selected Clubs */}
      {isLongFormat ? (
        /* LONG: club rows + start-day picker + the shared grid over a 7-day week */
        <>
          <div className="mt-4 space-y-2">
            {effectiveClubSchedules.map((clubSchedule) => (
              <SelectedClubRow
                key={clubSchedule.clubId}
                club={clubSchedule.club}
                sportUid={sportUid}
                onRemove={() => handleRemoveClub(clubSchedule.clubId)}
              />
            ))}
          </div>

          {longClub && isEditing && (
            <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                La grilla de horarios de un torneo largo no se puede cambiar después de crearlo.
                El cierre de inscripción se modifica en el paso anterior.
              </p>
            </div>
          )}

          {longClub && !isEditing && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Horarios de las fechas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="long-week-start">
                    ¿Qué día arranca la primera fecha?
                  </Label>
                  <DateInputWithCalendar
                    id="long-week-start"
                    value={weekStartDateValue}
                    onChange={handleWeekStartChange}
                    minDate={longMinStartDate}
                    isDateAllowed={isAllowedStartDay}
                    className="max-w-xs"
                    placeholder="dd/mm/aaaa"
                  />
                  <p className="text-sm text-muted-foreground">
                    La jornada 1 usará esta semana (este día y los 6 siguientes). Las
                    siguientes jornadas se repiten cada 7 días.
                  </p>
                </div>

                {gridConfig && (
                  <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>{describeAdoptedConfig(gridConfig)}</p>
                  </div>
                )}

                <WeeklyGridPicker
                  club={longClub}
                  sportUid={sportUid}
                  matchDuration={matchDuration}
                  weekStartDate={weekStartDateValue}
                  selectedGridId={data?.weeklyGridId}
                  onSelect={handleSelectGrid}
                />
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <div className="mt-4 space-y-4">
          {effectiveClubSchedules.map((clubSchedule) => (
            <ClubCard
              key={clubSchedule.clubId}
              clubSchedule={clubSchedule}
              schedules={schedulesCache[clubSchedule.clubId] || []}
              matchDuration={matchDuration}
              sportUid={sportUid}
              tournamentFormat={tournamentFormat}
              dateSelectionMode={dateSelectionMode}
              signupDeadlineDate={signupDeadlineDate}
              isLoading={loadingClubSchedules === clubSchedule.clubId}
              onToggleSchedule={(scheduleId) => handleToggleSchedule(clubSchedule.clubId, scheduleId)}
              onRemove={() => handleRemoveClub(clubSchedule.clubId)}
              onScheduleCreated={(schedule) => handleScheduleCreated(clubSchedule.clubId, schedule)}
              onLoadSchedules={() => fetchSchedulesForClub(clubSchedule.clubId)}
              minDate={
                draft?.tournamentConfig?.signupDeadlineDate
                  ? (() => {
                      const d = new Date(draft.tournamentConfig.signupDeadlineDate!);
                      d.setDate(d.getDate() + 1);
                      d.setHours(0, 0, 0, 0);
                      return d;
                    })()
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Help text */}
      {effectiveClubSchedules.length === 0 && (
        <p className="text-center text-sm text-gray-500">
          {isLongFormat
            ? 'Agregá al menos un club y definí los horarios semanales para continuar.'
            : 'Agrega al menos un club y selecciona sus bloques de horario disponibles para continuar.'}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// SELECTED CLUB ROW (lightweight, used for LONG)
// ============================================================================

interface SelectedClubRowProps {
  club: Club;
  sportUid?: string | null;
  onRemove: () => void;
}

function SelectedClubRow({ club, sportUid, onRemove }: SelectedClubRowProps) {
  const courtsCount = sportUid
    ? getCourtsCountForSport(club, sportUid)
    : club.courts?.length ?? 0;

  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
        <Building2 className="h-5 w-5 text-blue-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-gray-900">{club.name}</p>
        <p className="truncate text-sm text-gray-500">
          {club.address?.city || 'Sin ubicación'} · {courtsCount} canchas
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-red-500 hover:bg-red-50 hover:text-red-600"
        onClick={onRemove}
        aria-label="Quitar club"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ============================================================================
// SUMMARY CARD
// ============================================================================

interface SummaryCardProps {
  clubSchedules: ClubScheduleSelection[];
  schedulesCache: Record<string, Schedule[]>;
  matchDuration: number;
  /** SHORT: says where the bracket is played across every selected block. */
  showKnockouts: boolean;
}

function SummaryCard({ clubSchedules, schedulesCache, matchDuration, showKnockouts }: SummaryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedScheduleIds, setExpandedScheduleIds] = useState<Set<string>>(new Set());

  const totalSchedules = clubSchedules.reduce((acc, cs) => acc + cs.scheduleIds.length, 0);
  const totalTimeBlocks = clubSchedules.reduce((acc, cs) => {
    const schedules = schedulesCache[cs.clubId] || [];
    return acc + cs.scheduleIds.reduce((schedAcc, scheduleId) => {
      const schedule = schedules.find(s => s.id === scheduleId);
      const matching = schedule ? filterTimeBlocksByMatchDuration(schedule.timeBlocks, matchDuration) : [];
      return schedAcc + matching.length;
    }, 0);
  }, 0);
  const totalPartidos = clubSchedules.reduce((acc, cs) => {
    const schedules = schedulesCache[cs.clubId] || [];
    return acc + cs.scheduleIds.reduce((schedAcc, scheduleId) => {
      const schedule = schedules.find(s => s.id === scheduleId);
      const matching = schedule ? filterTimeBlocksByMatchDuration(schedule.timeBlocks, matchDuration) : [];
      return schedAcc + matching.reduce((blockAcc, b) => blockAcc + b.courtCount, 0);
    }, 0);
  }, 0);

  const hasClubsWithoutSchedules = clubSchedules.some(cs => cs.scheduleIds.length === 0);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  // Each block only knows its own days, so where the bracket really lands is only visible here, over every selected block.
  const knockoutsLabel = (() => {
    if (!showKnockouts) return null;
    const selected = clubSchedules.flatMap((cs) =>
      (schedulesCache[cs.clubId] || []).filter((s) => cs.scheduleIds.includes(s.id)),
    );
    const cut = earliestKnockoutStart(selected.map((s) => s.knockoutStart));
    if (cut) return `Llaves desde el ${formatKnockoutStart(cut, true)}`;
    const dates = [...new Set(selected.flatMap((s) => s.timeBlocks.map((b) => b.date)))].sort();
    return dates.length > 1
      ? `Llaves: todo el último día (${formatDate(dates[dates.length - 1])})`
      : null;
  })();

  const toggleScheduleExpanded = (scheduleId: string) => {
    setExpandedScheduleIds(prev => {
      const next = new Set(prev);
      if (next.has(scheduleId)) {
        next.delete(scheduleId);
      } else {
        next.add(scheduleId);
      }
      return next;
    });
  };

  const INITIAL_BLOCKS_SHOWN = 8;

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
      <CardContent className="p-0">
        <div
          className="cursor-pointer p-4"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-lg">
                <CalendarDays className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900 sm:text-lg">
                  {clubSchedules.length} club{clubSchedules.length !== 1 ? 's' : ''} · {totalSchedules} bloque{totalSchedules !== 1 ? 's' : ''} de horario
                </p>
                <p className="text-sm text-gray-600">
                  {totalTimeBlocks} horarios totales disponibles
                  {totalPartidos > 0 && (
                    <> · {totalPartidos} partido{totalPartidos !== 1 ? 's' : ''}</>
                  )}
                </p>
                {knockoutsLabel && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-amber-800">
                    <Flag className="h-3.5 w-3.5 shrink-0" />
                    {knockoutsLabel}
                  </p>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="shrink-0 gap-1">
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  <span className="hidden sm:inline">Ocultar</span>
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  <span className="hidden sm:inline">Ver detalle</span>
                </>
              )}
            </Button>
          </div>
          {hasClubsWithoutSchedules && (
            <div className="mt-2">
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                Algunos clubs sin bloques de horario
              </Badge>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="border-t border-blue-200 bg-white p-4">
            <div className="space-y-6">
              {clubSchedules.map((cs) => {
                const schedules = schedulesCache[cs.clubId] || [];
                const selectedSchedules = schedules.filter(s => cs.scheduleIds.includes(s.id));
                
                if (selectedSchedules.length === 0) {
                  return (
                    <div key={cs.clubId} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-amber-600" />
                        <span className="font-medium text-amber-800">{cs.club.name}</span>
                        <span className="text-sm text-amber-600">— Sin bloques de horario seleccionados</span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={cs.clubId} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">{cs.club.name}</h4>
                      <Badge variant="secondary" className="ml-2">
                        {selectedSchedules.length} bloque{selectedSchedules.length !== 1 ? 's' : ''} de horario
                      </Badge>
                    </div>

                    <div className="ml-7 space-y-3">
                      {selectedSchedules.map((schedule) => {
                        const matchingBlocks = filterTimeBlocksByMatchDuration(schedule.timeBlocks, matchDuration);
                        const isScheduleExpanded = expandedScheduleIds.has(schedule.id);
                        const blocksToShow = isScheduleExpanded 
                          ? matchingBlocks 
                          : matchingBlocks.slice(0, INITIAL_BLOCKS_SHOWN);
                        const hasMoreBlocks = matchingBlocks.length > INITIAL_BLOCKS_SHOWN;
                        const hiddenCount = matchingBlocks.length - INITIAL_BLOCKS_SHOWN;

                        const scheduleDates = [...new Set(matchingBlocks.map(b => b.date))].sort();
                        const scheduleDateRange = scheduleDates.length > 0
                          ? scheduleDates.length === 1
                            ? formatDate(scheduleDates[0])
                            : `${formatDate(scheduleDates[0])} - ${formatDate(scheduleDates[scheduleDates.length - 1])}`
                          : '';

                        return (
                          <div key={schedule.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-800">{schedule.title}</span>
                                <p className="text-xs text-gray-500">{scheduleDateRange}</p>
                              </div>
                              <Badge variant="outline">{matchingBlocks.length} horarios</Badge>
                            </div>
                            
                            <div className="grid gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                              {blocksToShow.map((block) => (
                                <div
                                  key={block.id}
                                  className="flex items-center justify-between rounded bg-white px-2 py-1.5 text-xs shadow-sm"
                                >
                                  <span className="font-medium text-gray-700">
                                    {formatDate(block.date)}
                                  </span>
                                  <span className="text-gray-500">
                                    {block.startTime} - {block.endTime}
                                  </span>
                                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                                    {block.courtCount}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                            
                            {hasMoreBlocks && (
                              <button
                                onClick={() => toggleScheduleExpanded(schedule.id)}
                                className="mt-2 flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                {isScheduleExpanded ? (
                                  <>
                                    <ChevronUp className="h-3 w-3" />
                                    Mostrar menos
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3" />
                                    Ver {hiddenCount} horarios más
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// CLUB CARD
// ============================================================================

interface ClubCardProps {
  clubSchedule: ClubScheduleSelection;
  schedules: Schedule[];
  matchDuration: number;
  sportUid?: string | null;
  /** Tournament format, for the schedule compatibility rule. */
  tournamentFormat?: TournamentFormat | null;
  signupDeadlineDate?: Date | string | null;
  isLoading: boolean;
  onToggleSchedule: (scheduleId: string) => void;
  onRemove: () => void;
  onScheduleCreated: (schedule: Schedule) => void;
  onLoadSchedules: () => void;
  /** Min date for new block range (e.g. day after signup deadline). */
  minDate?: Date;
  /** Calendar mode for the create-block form. Default: 'range'. */
  dateSelectionMode?: ScheduleDatesMode;
}

function ClubCard({
  clubSchedule,
  schedules,
  matchDuration,
  sportUid,
  tournamentFormat,
  signupDeadlineDate,
  isLoading,
  onToggleSchedule,
  onRemove,
  onScheduleCreated,
  onLoadSchedules: _onLoadSchedules,
  minDate,
  dateSelectionMode = 'range',
}: ClubCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);

  const selectedCount = clubSchedule.scheduleIds.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div
            className="flex flex-1 cursor-pointer items-center gap-3"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{clubSchedule.club.name}</h3>
              <p className="text-sm text-gray-500">
                {selectedCount} bloque{selectedCount !== 1 ? 's' : ''} de horario seleccionado{selectedCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-500">Cargando bloques de horario...</span>
            </div>
          ) : schedules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
              <Calendar className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                No hay bloques de horario disponibles para este club
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowCreateSchedule(true)}
              >
                <Plus className="mr-1 h-4 w-4" />
                Crear bloque de horario
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                {(() => {
                  const durationMatching = schedules.filter(
                    (s) => filterTimeBlocksByMatchDuration(s.timeBlocks, matchDuration).length > 0
                  );
                  const afterDeadline = durationMatching.filter((s) =>
                    isScheduleStartAfterDeadline(s, signupDeadlineDate)
                  );
                  const eligible = afterDeadline.filter((s) =>
                    isScheduleCompatibleWithTournament(s, { sportUid, format: tournamentFormat })
                  );

                  if (durationMatching.length === 0) {
                    return (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
                        Ningún bloque de horario de este club tiene franjas de {matchDuration} min. Creá uno nuevo con esa duración.
                      </p>
                    );
                  }

                  if (eligible.length === 0 && afterDeadline.length === 0 && signupDeadlineDate) {
                    return (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
                        No hay bloques disponibles posteriores a la fecha de cierre de inscripción.
                      </p>
                    );
                  }

                  if (eligible.length === 0 && afterDeadline.length > 0) {
                    return (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
                        Los bloques de horario de este club ya están siendo usados por torneos de otro deporte o de otro formato. Un bloque solo se puede compartir entre torneos del mismo deporte y el mismo formato.
                      </p>
                    );
                  }

                  return eligible.map((schedule) => (
                    <ScheduleItemExpandable
                      key={schedule.id}
                      schedule={schedule}
                      matchDuration={matchDuration}
                      isSelected={clubSchedule.scheduleIds.includes(schedule.id)}
                      onToggle={() => onToggleSchedule(schedule.id)}
                    />
                  ));
                })()}
              </div>

              <Separator />

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowCreateSchedule(true)}
              >
                <Plus className="mr-1 h-4 w-4" />
                Crear nuevo bloque de horario
              </Button>
            </div>
          )}

          {showCreateSchedule && (
            <div className="mt-4 rounded-lg border bg-card p-4">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="font-semibold">Crear nuevo bloque de horario</h4>
                <Button variant="ghost" size="icon" onClick={() => setShowCreateSchedule(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CreateScheduleBlockForm
                club={clubSchedule.club}
                matchDuration={matchDuration}
                sportUid={sportUid}
                onClose={() => setShowCreateSchedule(false)}
                minDate={minDate}
                dateSelectionMode={dateSelectionMode}
                allowKnockoutStart={tournamentFormat === TournamentFormat.SHORT}
                onSubmitRequest={async (request) => {
                  try {
                    const created = await createSchedule(clubSchedule.club.id, request);
                    onScheduleCreated(transformApiScheduleToUI(created));
                  } catch (err) {
                    handleGlobalError(err, 'Error', 'No se pudo crear el bloque de horario');
                  }
                }}
              />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ============================================================================
// SCHEDULE ITEM EXPANDABLE
// ============================================================================

interface ScheduleItemExpandableProps {
  schedule: Schedule;
  matchDuration: number;
  isSelected: boolean;
  onToggle: () => void;
}

function ScheduleItemExpandable({ schedule, matchDuration, isSelected, onToggle }: ScheduleItemExpandableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);

  const matchingBlocks = filterTimeBlocksByMatchDuration(schedule.timeBlocks, matchDuration);

  const INITIAL_DATES_SHOWN = 5;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
    });
  };

  const blocksByDate = matchingBlocks.reduce((acc, block) => {
    if (!acc[block.date]) {
      acc[block.date] = [];
    }
    acc[block.date].push(block);
    return acc;
  }, {} as Record<string, TimeBlock[]>);

  const dates = Object.keys(blocksByDate).sort();
  const datesToShow = showAllDates ? dates : dates.slice(0, INITIAL_DATES_SHOWN);
  const hasMoreDates = dates.length > INITIAL_DATES_SHOWN;
  const hiddenDatesCount = dates.length - INITIAL_DATES_SHOWN;

  const dateRange = dates.length > 0 
    ? dates.length === 1 
      ? formatDateShort(dates[0])
      : `${formatDateShort(dates[0])} - ${formatDateShort(dates[dates.length - 1])}`
    : '';

  const totalCourts = matchingBlocks.reduce((sum, b) => sum + b.courtCount, 0);
  const knockoutLabel = knockoutBlockLabel(schedule.knockoutStart, schedule.timeBlocks);

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        isSelected
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          className="mt-0.5 h-5 w-5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        />

        <div
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-2"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className={cn(
                'font-medium',
                isSelected ? 'text-blue-900' : 'text-gray-900'
              )}>
                {schedule.title}
              </p>
              {knockoutLabel && (
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-500 text-xs text-amber-700"
                >
                  {knockoutLabel}
                </Badge>
              )}
              {isSelected && (
                <Badge className="shrink-0 bg-blue-600 text-xs">Seleccionado</Badge>
              )}
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
              {matchingBlocks.length} horarios · {totalCourts} partido
              {totalCourts !== 1 ? 's' : ''} · {dates.length} día
              {dates.length !== 1 ? 's' : ''} · {dateRange}
            </p>
          </div>

          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-white p-3">
          <div className="space-y-3">
            {datesToShow.map((date) => {
              const blocks = blocksByDate[date];
              return (
                <div key={date} className="space-y-1">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    {formatDate(date)}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {blocks.map((block) => (
                      <div
                        key={block.id}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                          isSelected ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                        )}
                      >
                        <Clock className="h-3 w-3" />
                        <span>{block.startTime} - {block.endTime}</span>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'h-4 px-1 text-[10px]',
                            isSelected && 'bg-blue-200',
                          )}
                        >
                          {block.courtCount} partido
                          {block.courtCount === 1 ? '' : 's'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {hasMoreDates && (
              <button
                onClick={() => setShowAllDates(!showAllDates)}
                className="flex w-full items-center justify-center gap-1 rounded-md py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
              >
                {showAllDates ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Mostrar menos días
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    Ver {hiddenDatesCount} días más
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export default ClubSchedulesStep;
